import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  RudderstackCredentials,
  rudderstackCredentialsSchema,
} from '@tinct/core-api-models';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useCreateCrmIntegration,
  useGetCrmIntegrationAuthInfo,
  useUpdateCrmIntegrationAuthInfo,
} from '@tinct/core-api';
import { handleApiError } from '@tinct/web-form';

type UseRudderstackConfigFormOptions = {
  organizationId: string;
  // When provided, the form edits an existing integration's auth info instead
  // of creating a new one.
  integrationId?: string;
  onSuccess?: () => void;
};

export const useRudderstackConfigForm = ({
  organizationId,
  integrationId,
  onSuccess,
}: UseRudderstackConfigFormOptions) => {
  const isEditing = !!integrationId;

  const form = useForm<RudderstackCredentials>({
    resolver: zodResolver(rudderstackCredentialsSchema),
    defaultValues: {
      writeKey: '',
      dataPlaneUrl: '',
    },
  });

  const { data: authInfo } = useGetCrmIntegrationAuthInfo(integrationId);

  useEffect(() => {
    if (!integrationId) {
      // Not editing (e.g. after the integration was disconnected): keep the
      // form empty so re-connecting starts from a clean slate.
      form.reset({ writeKey: '', dataPlaneUrl: '' });
      return;
    }
    if (!authInfo) return;
    form.reset({
      writeKey: authInfo.writeKey ?? '',
      dataPlaneUrl: authInfo.dataPlaneUrl ?? '',
    });
  }, [integrationId, authInfo, form]);

  const createCrmIntegration = useCreateCrmIntegration({
    onSuccess: () => {
      form.reset();
      onSuccess?.();
    },
    onError: (error) => handleApiError(error, form),
  });

  const updateAuthInfo = useUpdateCrmIntegrationAuthInfo({
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => handleApiError(error, form),
  });

  const handleSubmit = form.handleSubmit((data) => {
    if (isEditing && integrationId) {
      // The read endpoint returns the write key masked; only re-send the secret
      // when the user actually changed it, otherwise the backend keeps it.
      const maskedWriteKey = authInfo?.writeKey ?? '';
      const writeKeyChanged = data.writeKey !== maskedWriteKey;
      const credentials: Record<string, string> = {
        dataPlaneUrl: data.dataPlaneUrl,
        // Omit the write key when unchanged so the stored secret is kept.
        ...(writeKeyChanged ? { writeKey: data.writeKey } : {}),
      };
      updateAuthInfo.mutate({ integrationId, organizationId, credentials });
      return;
    }

    createCrmIntegration.mutate({
      organizationId,
      providerType: 'rudderstack',
      credentials: {
        writeKey: data.writeKey,
        dataPlaneUrl: data.dataPlaneUrl,
      },
    });
  });

  const reset = () => {
    if (isEditing && authInfo) {
      // Restore the fetched configuration rather than clearing it, so reopening
      // the modal shows the current values again.
      form.reset({
        writeKey: authInfo.writeKey ?? '',
        dataPlaneUrl: authInfo.dataPlaneUrl ?? '',
      });
      return;
    }
    form.reset();
  };

  return {
    form,
    handleSubmit,
    isEditing,
    isLoading: createCrmIntegration.isPending || updateAuthInfo.isPending,
    isSuccess: createCrmIntegration.isSuccess || updateAuthInfo.isSuccess,
    reset,
  };
};
